// Commitment Contract Modal

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Custom
import CustomInput from "../../forms/input/Input";
import DatePickerInput from "../../forms/input/DatePickerInput";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearchBox from "../search/SearchBox";
import CustomSearch from "../search/Search";
import CustomChip from "../chip/Chip";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { useCustomFields } from "../../context/CustomFieldsProvider";
import { ToastState } from "../../types";

const CommitmentContractModal = ({
  openModal,
  setOpenModal,
  setToast,
  addOrEdit,
  nodes,
  setNodes,
  handleCustomerAndAssetSearch,
}: {
  openModal: { id: string; type: string };
  setOpenModal: (value: { id: string; type: string }) => void;
  setToast: (value: ToastState) => void;
  addOrEdit: "add" | "edit" | null;
  nodes: { id: string; type: string }[];
  setNodes: (value: { id: string; type: string }[]) => void;
  handleCustomerAndAssetSearch: () => void;
}) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Contract Details");
  const [ruleBook, setRuleBook] = useState([
    {
      label: "TEST_RULE_BOOK2",
      value: "044cb970-133b-11ef-a093-1127a7ee82a3",
    },
    {
      label: "TEST_RULE_BOOK1",
      value: "348958b0-12bd-11ef-a093-1127a7ee82a3",
    },
    {
      label: "TEST_RULE_BOOK1",
      value: "64bcc530-12b8-11ef-a093-1127a7ee82a3",
    },
    {
      label: "TEST_RULE_BOOK1",
      value: "c3967710-12c5-11ef-a093-1127a7ee82a3",
    },
  ]);
  const [names, setNames] = useState<string[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<any[]>([]);
  const [contractData, setContractData] = useState<{
    id: { id: string; entityType: string };
    consumerId: string;
    siteId: string;
    startDate: Date | null;
    endDate: Date | null;
    commodityType: { value: string; label: string };
    commodityCategory: { value: string; label: string };
    registerRuleBook: { value: string; label: string };
    subscribedVoltage: string;
    subscribedTariff: string;
    billingCycle: string;
    producerPremise: string;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    consumerId: "",
    siteId: "",
    startDate: null,
    endDate: null,
    commodityType: { value: "", label: "" },
    commodityCategory: { value: "", label: "" },
    registerRuleBook: { value: "", label: "" },
    subscribedVoltage: "",
    subscribedTariff: "",
    billingCycle: "",
    producerPremise: "",
  });
  const [billingData, setBillingData] = useState<{
    fromDate: Date | null;
    toDate: Date | null;
    billingStatus: string;
  }>({
    fromDate: null,
    toDate: null,
    billingStatus: "",
  });

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const location = useLocation();

  const startDate = moment(contractData?.startDate);
  const today = moment();
  const isActive = startDate.isBefore(today);

  const navigate = useNavigate();
  const TABS = ["Contract Details", "Billing Determinants"];

  const determinantStatusOptions = [
    { value: "Initial", label: "Initial" },
    { value: "Success", label: "Success" },
    { value: "Rejected", label: "Rejected" },
  ];

  const commodityTypeOptions = [
    { value: "Electricity", label: "Electricity" },
    { value: "Water", label: "Water" },
    { value: "Gas", label: "Gas" },
  ];

  const commodityCategoryOptions = [
    { value: "Residential", label: "Residential" },
    { value: "Commercial", label: "Commercial" },
  ];

  const generateUniqueName = () => {
    const randomSuffix = Math.floor(Math.random() * 1000000000);
    const newName = `contract${randomSuffix}`;
    setNames([...names, newName]);
    return newName;
  };

  const handleSubmit = async () => {
    if (contractData?.consumerId) {
      try {
        const requestData = {
          id: {
            id: contractData?.id?.id,
            entityType: "ASSET",
          },
          type: "CONTRACT",
          tenantId: {
            id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
            entityType: "TENANT",
          },
          name: generateUniqueName(),
          label: "NY Building",
          additionalInfo: {
            consumerid: contractData.consumerId,
            siteId: contractData.siteId,
            startdate: contractData.startDate,
            enddate: contractData.endDate,
            commodityType: contractData.commodityType?.value,
            commodityCategory: contractData.commodityCategory?.value,
            registerRuleBook: contractData.registerRuleBook?.value,
            subscribedVoltage: contractData.subscribedVoltage,
            subscribedTariff: contractData.subscribedTariff,
            billingCycle: contractData.billingCycle,
            producerPremise: contractData.producerPremise,
          },
        };

        if (contractData?.id && contractData?.id?.id && addOrEdit === "edit") {
          requestData.id = {
            id: contractData?.id?.id,
            entityType: "ASSET",
          };
        }

        if (contractData?.consumerId) {
          let secondResponse = null;
          const response = await fetchPOSTRequest(`asset`, requestData);

          if (response) {
            secondResponse = await fetchPOSTRequest(
              `customer/${contractData?.consumerId}/asset/${response?.id?.id}`,
              requestData,
              true,
            );
          }

          setOpenModal({
            type: "",
            id: "",
          });

          setSearchValue({ ...searchValue, value: response?.id?.id });

          if (addOrEdit === "add") {
            const updatedNodes = nodes?.map((node: any) => {
              if (node?.id?.toUpperCase() === "CONTRACT") {
                const newObj = {
                  created: true,
                  label: "Successfully created",
                  link: "",
                  title: "Contract",
                  value: response?.id?.id,
                };
                return {
                  ...node,
                  data: newObj,
                };
              }
              return node;
            });

            setNodes([...updatedNodes]);
          } else {
            handleCustomerAndAssetSearch();
          }

          setContractData({
            id: { id: "", entityType: "" },
            consumerId: "",
            siteId: "",
            startDate: null,
            endDate: null,
            commodityType: { value: "", label: "" },
            commodityCategory: { value: "", label: "" },
            registerRuleBook: { value: "", label: "" },
            subscribedVoltage: "",
            subscribedTariff: "",
            billingCycle: "",
            producerPremise: "",
          });

          setOpenModal({
            type: "",
            id: "",
          });

          if (secondResponse?.status === 200) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: response?.id?.id + " " + "Update successful!",
            });
          } else {
            setToast({
              message: response?.message,
              severity: "error",
            });
          }
        } else {
          setToast({
            message: "Consumer ID Field is Required",
            severity: "error",
          });
        }
      } catch (error: any) {
        console.error("Error saving data:", error.message);
      }
    } else {
      setToast({
        message: "Consumer ID Field is Required",
        severity: "error",
      });
    }
  };

  const handleContractSearch = async (payload?: string) => {
    try {
      let endpoint;
      if (searchValue?.value.trim()) {
        endpoint = searchValue?.value;
      } else if (payload) {
        endpoint = payload;
      } else {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${endpoint}`, {}, true);

      if (response?.data?.additionalInfo) {
        const {
          commodityCategory,
          commodityType,
          consumerid,
          enddate,
          registerRuleBook,
          siteId,
          startdate,
          subscribedVoltage,
          subscribedTariff,
          billingCycle,
          producerPremise,
        } = response?.data?.additionalInfo;

        setContractData({
          id: response?.data?.id,
          consumerId: consumerid,
          siteId: siteId,
          startDate: startdate ? new Date(startdate) : null,
          endDate: enddate ? new Date(enddate) : null,
          commodityType: commodityTypeOptions.find(
            (item) => item.value === commodityType,
          ) || { value: "", label: "" },
          commodityCategory: commodityCategoryOptions.find(
            (item) => item.value === commodityCategory,
          ) || { value: "", label: "" },
          registerRuleBook: ruleBook.find(
            (item) => item.value === registerRuleBook,
          ) || { value: "", label: "" },
          subscribedVoltage: subscribedVoltage,
          subscribedTariff: subscribedTariff,
          billingCycle: billingCycle,
          producerPremise: producerPremise,
        });
      } else {
        setToast({ message: "Contract not found", severity: "error" });
      }
      setSearchOpen(false);
    } catch (error: any) {
      console.error("Error fetching consumer:", error.message);
      setSearchOpen(false);
    }
  };

  useEffect(() => {
    if (openModal?.id && openModal?.type === "CONTRACT") {
      setSearchValue({ ...searchValue, value: openModal?.id });
      handleContractSearch(openModal?.id);
    }
  }, [openModal]);

  useEffect(() => {
    if (location?.state?.contractId) {
      setSearchValue({ ...searchValue, value: location?.state?.contractId });
      handleContractSearch(location?.state?.contractId);
    }
  }, [location]);

  const searchBillings = async () => {
    try {
      const allAssets = [];
      let page = 0;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await fetchGETRequest(
          `tenant/assets?page=${page}&pageSize=10`,
          {},
          true,
        );

        if (response?.status === 200) {
          const assets = response?.data?.data;

          if (assets && Array.isArray(assets)) {
            allAssets.push(...assets);
            hasMorePages = assets.length > 0;
            page += 1;
          } else {
            hasMorePages = false;
          }
        } else {
          hasMorePages = false;
        }
      }

      const filteredAssets = allAssets.filter(
        (asset) =>
          asset.type === "billDeterminant" &&
          contractData.id.id === asset.additionalInfo.contractid &&
          new Date(asset.additionalInfo?.billPeriodFrom) >=
            new Date(billingData.fromDate || "") &&
          new Date(asset.additionalInfo?.billPeriodTo) <=
            new Date(billingData.toDate || ""),
      );

      setFilteredAssets(filteredAssets);
    } catch (error: any) {
      console.error("Error fetching assets:", error.message);
    }
  };

  const handleGoToBillDeterminant = (billDeterminantId: string) => {
    navigate("/billing-determinant", { state: { billDeterminantId } });
  };

  const handleGoToConsumer = (consumerId: string) => {
    navigate("/consumer", {
      state: { consumerId: consumerId, contractId: contractData?.id?.id },
    });
  };

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={contractData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setContractData({
                      ...contractData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={contractData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setContractData({
                  ...contractData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={contractData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setContractData({
                  ...contractData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={contractData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setContractData({
                  ...contractData,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  const handleGoToSite = (siteId: string) => {
    navigate("/site", { state: { siteId: siteId } });
  };

  return (
    <Dialog
      onClose={() => setOpenModal({ type: "", id: "" })}
      open={openModal?.type === "CONTRACT"}
      fullWidth
      maxWidth={"md"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Commitment Contract Information
        </DialogTitle>
        <IconButton
          onClick={() => setOpenModal({ type: "", id: "" })}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Card
          sx={{
            overflow: "visible",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" fontWeight={600}>
            Commitment Contract ID
          </Typography>

          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            <CustomSearchBox
              placeholder={"Enter 10 Digit"}
              width={"500px"}
              height={"40px"}
              handleSearchToggle={() => setSearchOpen(!searchOpen)}
              searchOpen={searchOpen}
              dialogBody={
                <Stack direction={"column"} gap={"20px"}>
                  <CustomInput
                    label={"Type"}
                    value={searchValue?.type}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        type: e.target.value,
                      })
                    }
                  />
                  <CustomInput
                    label={"Value"}
                    value={searchValue?.value}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        value: e.target.value,
                      })
                    }
                  />
                  <ContainedButton
                    label={"Submit"}
                    sx={{
                      width: "100%",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={handleContractSearch}
                  />
                </Stack>
              }
            />
          </Stack>
        </Card>

        <Card sx={{ mt: "20px" }}>
          <TabContext value={activeTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={(_, newValue) => setActiveTab(newValue)}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "##1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((item, index) => {
                  return (
                    <Tab
                      key={index}
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={"7px"}
                          fontWeight={500}
                        >
                          {item}
                        </Box>
                      }
                      value={item}
                      className="consumer_right_card_list_tab"
                    />
                  );
                })}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => toggleCustomFieldDrawer("Contract", true)}
              />
            </Box>

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Stack direction={"row"} gap={"5px"}>
                  <Typography variant="body1">Status:</Typography>
                  <CustomChip
                    label={isActive ? "Active " : "Inactive"}
                    sx={{
                      minWidth: "auto",
                    }}
                    backgroundColor={isActive ? "#9CE976" : "#FF3B30"}
                    color="#ffffff"
                  />
                </Stack>

                <Grid container spacing={2} sx={{ mt: "10px" }}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Typography variant="body1" mb={"10px"}>
                      Consumer ID *
                    </Typography>
                    <CustomSearch
                      placeholder={"Enter 10 Digit"}
                      sx={{
                        width: "100%",
                        height: "40px",
                      }}
                      name={"consumerId"}
                      onChange={(e) => {
                        setContractData({
                          ...contractData,
                          consumerId: e.target.value,
                        });
                      }}
                      value={contractData?.consumerId}
                      onClick={() =>
                        handleGoToConsumer(contractData?.consumerId)
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <Typography variant="body1" mb={"10px"}>
                      Site ID
                    </Typography>
                    <CustomSearch
                      placeholder={"Enter 10 Digit"}
                      sx={{
                        width: "100%",
                        height: "40px",
                      }}
                      name={"siteId"}
                      onChange={(e) => {
                        setContractData({
                          ...contractData,
                          siteId: e.target.value,
                        });
                      }}
                      value={contractData?.siteId}
                      onClick={() => handleGoToSite(contractData?.siteId)}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} mt={"20px"}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"Contract Start Date"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"startDate"}
                      onChange={(event) => {
                        setContractData({
                          ...contractData,
                          startDate: event,
                        });
                      }}
                      value={contractData?.startDate}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"Contract End Date:"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"endDate"}
                      onChange={(event) => {
                        setContractData({
                          ...contractData,
                          endDate: event,
                        });
                      }}
                      value={contractData?.endDate}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: "20px" }}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomOutlinedSelect
                      label={"Commodity Type"}
                      options={commodityTypeOptions}
                      placeholder={"Select Commodity Type"}
                      name={"commodityType"}
                      onChange={(newValue) => {
                        setContractData({
                          ...contractData,
                          commodityType: newValue as {
                            value: string;
                            label: string;
                          },
                        });
                      }}
                      value={contractData?.commodityType}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomOutlinedSelect
                      label={"Commodity Category"}
                      options={commodityCategoryOptions}
                      placeholder={"Select Commodity Category"}
                      name={"commodityCategory"}
                      onChange={(newValue) => {
                        setContractData({
                          ...contractData,
                          commodityCategory: newValue as {
                            value: string;
                            label: string;
                          },
                        });
                      }}
                      value={contractData?.commodityCategory}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomOutlinedSelect
                      label={"Register Rule Book"}
                      options={ruleBook}
                      placeholder={"Select"}
                      name={"registerRuleBook"}
                      onChange={(newValue) => {
                        setContractData({
                          ...contractData,
                          registerRuleBook: newValue as {
                            value: string;
                            label: string;
                          },
                        });
                      }}
                      value={contractData?.registerRuleBook}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Subscribed voltage"}
                      placeholder={"Subscribed voltage"}
                      name={"subscribedVoltage"}
                      onChange={(e) => {
                        setContractData({
                          ...contractData,
                          subscribedVoltage: e.target.value,
                        });
                      }}
                      value={contractData?.subscribedVoltage}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Subscribed Tariff"}
                      placeholder={"Subscribed Tariff"}
                      name={"subscribedTariff"}
                      onChange={(e) => {
                        setContractData({
                          ...contractData,
                          subscribedTariff: e.target.value,
                        });
                      }}
                      value={contractData?.subscribedTariff}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Billing cycle"}
                      placeholder={"Billing cycle"}
                      name={"billingCycle"}
                      onChange={(e) => {
                        setContractData({
                          ...contractData,
                          billingCycle: e.target.value,
                        });
                      }}
                      value={contractData?.billingCycle}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Producer premise"}
                      placeholder={"Producer premise"}
                      name={"producerPremise"}
                      onChange={(e) => {
                        setContractData({
                          ...contractData,
                          producerPremise: e.target.value,
                        });
                      }}
                      value={contractData?.producerPremise}
                    />
                  </Grid>
                </Grid>

                {createdFields?.["Contract"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Contract"]?.map(
                        (item: any, index: number) => {
                          return (
                            <Grid
                              size={{ xs: 12, md: 4, lg: 4 }}
                              mt={"10px"}
                              key={index}
                            >
                              {getCustomFields(item)}
                            </Grid>
                          );
                        },
                      )}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                >
                  <ContainedButton
                    label={"Add Billing Determinant"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={() => navigate("/billing-determinant")}
                  />
                </Stack>

                <Grid container spacing={2} mt={"20px"}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"From Date:"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"fromDate"}
                      value={billingData?.fromDate}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          fromDate: newValue,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"To Date:"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"toDate"}
                      value={billingData?.toDate}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          toDate: newValue,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      placeholder={"Status"}
                      options={determinantStatusOptions}
                      name={"billingStatus"}
                      value={billingData?.billingStatus}
                      label={"Billing Determinant Status"}
                      onChange={(newValue) => {
                        setBillingData({
                          ...billingData,
                          billingStatus: newValue.value as string,
                        });
                      }}
                    />
                  </Grid>
                </Grid>

                <ContainedButton
                  label={"Search"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    mt: "20px",
                    color: "#ffffff",
                  }}
                  onClick={searchBillings}
                />

                <Grid container spacing={2} mt={"20px"}>
                  {filteredAssets
                    .filter(
                      (asset) =>
                        asset.additionalInfo?.contractid ===
                        contractData?.id?.id,
                    )
                    .map((item, index) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} key={index}>
                          <Box
                            sx={{
                              background: "#e3f1fa",
                              mt: "20px",
                              padding: "15px",
                              borderRadius: "6px",
                            }}
                          >
                            <Stack
                              direction={"row"}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={"400"}
                                fontSize={"13px"}
                              >
                                Bill Determinant ID:
                              </Typography>
                              <Typography variant="body2" fontWeight={"600"}>
                                {item?.id?.id}
                              </Typography>
                            </Stack>

                            <Typography
                              variant="body1"
                              mt={"5px"}
                              fontSize={"13px"}
                              fontWeight={"400"}
                            >
                              {" "}
                              From: {item?.additionalInfo?.billPeriodFrom}
                            </Typography>
                            <Typography
                              variant="body1"
                              mt={"5px"}
                              fontSize={"13px"}
                              fontWeight={"400"}
                            >
                              {" "}
                              To: {item?.additionalInfo?.billPeriodTo}
                            </Typography>

                            <Stack
                              direction={"row"}
                              justifyContent={"flex-end"}
                            >
                              <ContainedButton
                                label={"Go To Bill Determinant"}
                                sx={{
                                  width: "fit-content",
                                  fontWeight: 600,
                                  mt: "10px",
                                  color: "#ffffff",
                                }}
                                onClick={() =>
                                  handleGoToBillDeterminant(item?.id?.id)
                                }
                              />
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                </Grid>
              </TabPanel>

              {/* <Stack direction={"direction"} justifyContent={"space-between"} justifyItems={"center"} mt={"20px"}>

              <ContainedButton
                label={"Go to Consumer"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => handleGoToConsumer(contractData?.consumerId)}
              />

              <ContainedButton
                label={"Go to Site"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={()=> handleGoToSite(contractData?.siteId)}
              />

            </Stack> */}
            </CardContent>
          </TabContext>
        </Card>

        <DialogActions>
          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
            disabled={!contractData?.consumerId}
            onClick={handleSubmit}
          />
          <ContainedButton
            label={"Close"}
            bgColor={"linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"}
            sx={{
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={() => setOpenModal({ type: "", id: "" })}
          />
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default CommitmentContractModal;
